import { useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Clipboard,
  File,
  FileText,
  Image,
  Loader2,
  UploadCloud,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import cssClass from './ContentUploadModel.module.css';
import { RoadmapContext } from '../../context/RoadmapContext';
import { UserContext } from '../../context/UserContext';
import PropTypes from 'prop-types';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB

const DEFAULT_CONFIG = {
  allowView: true,
  allowDownload: false,
  allowPrint: false,
};

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(file) {
  if (file.type.startsWith('image/')) return Image;
  if (file.type.includes('pdf') || file.type.includes('text')) return FileText;
  return File;
}

const animationVariants = {
  backdrop: {
    hidden: {
      opacity: 0,
      transition: { duration: 0.18 },
    },
    visible: {
      opacity: 1,
      transition: { duration: 0.18 },
    },
  },
  modal: {
    hidden: {
      scale: 0.96,
      opacity: 0,
      transition: { duration: 0.2, ease: 'easeInOut' },
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.22, ease: 'easeOut' },
    },
  },
};

const ContentUploadModel = ({ onClose, onUploadSuccess }) => {
  const { setShowContentUploadModal, contentUploadDetails } =
    useContext(RoadmapContext);
  const { userDetails } = useContext(UserContext);
  const { roadmap_id } = useParams();

  const inputRef = useRef(null);

  const [title, setTitle] = useState(contentUploadDetails?.title || '');
  const [file, setFile] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configOpen, setConfigOpen] = useState(false);

  const [uploadState, setUploadState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setTitle('');
    setFile(null);
    setConfig(DEFAULT_CONFIG);
    setConfigOpen(false);
    setUploadState('idle');
    setProgress(0);
    setUploadedUrl('');
    setCopied(false);
    setError(null);
  };

  const handleClose = () => {
    if (uploadState === 'uploading') return;
    reset();
    setShowContentUploadModal(false);
    if (onClose) onClose();
  };

  const selectFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setError(null);
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFileChange = (event) => {
    selectFile(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (uploadState !== 'idle') return;
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return;

    setUploadState('uploading');
    setProgress(0);
    setError(null);

    const email = userDetails?.email;

    try {
      // Step 1: Init
      const initRes = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/content/upload/init`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            e: email,
            title: title.trim(),
            file: { name: file.name, size: file.size, type: file.type },
            r_id: roadmap_id,
            n_id: contentUploadDetails?.node_id,
            config: {
              allow_view: config.allowView,
              allow_download: config.allowDownload,
              allow_print: config.allowPrint,
            },
          }),
        },
      );

      const initData = await initRes.json();
      if (!initRes.ok) {
        console.log(initData);
        throw new Error(
          initData?.error || 'Failed to initialize upload. Please try again.',
        );
      }

      const { upload_url, content_id } = initData;

      // Step 2: Upload file to blob storage
      if (file.size <= CHUNK_SIZE) {
        // Small file — single PUT with progress
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', upload_url);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader(
            'Content-Type',
            file.type || 'application/octet-stream',
          );

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setProgress(Math.round((e.loaded / e.total) * 100));
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(
                new Error(
                  `Upload failed with status ${xhr.status}. Please try again.`,
                ),
              );
            }
          };

          xhr.onerror = () =>
            reject(
              new Error(
                'Network error during upload. Please check your connection.',
              ),
            );

          xhr.send(file);
        });
      } else {
        // Large file — chunked Put Block + Put Block List
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const blockIds = [];

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);

          const blockId = btoa(String(i).padStart(8, '0'));
          blockIds.push(blockId);

          const chunkRes = await fetch(
            `${upload_url}&comp=block&blockid=${encodeURIComponent(blockId)}`,
            {
              method: 'PUT',
              headers: {
                // 'x-ms-blob-type': 'BlockBlob',
                'Content-Type': file.type || 'application/octet-stream',
                // 'Content-Length': String(end - start),
              },
              body: chunk,
            },
          );

          if (!chunkRes.ok) {
            const errBody = await chunkRes.json().catch(() => ({}));
            throw new Error(
              errBody?.message ||
                `Chunk ${i + 1}/${totalChunks} upload failed.`,
            );
          }

          setProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        // Commit block list
        const blockListXml =
          '<?xml version="1.0" encoding="utf-8"?>' +
          '<BlockList>' +
          blockIds.map((id) => `<Latest>${id}</Latest>`).join('') +
          '</BlockList>';

        const commitRes = await fetch(`${upload_url}&comp=blocklist`, {
          method: 'PUT',
          headers: {
            // 'x-ms-blob-type': 'BlockBlob',
            'Content-Type': 'application/xml',
          },
          body: blockListXml,
        });

        if (!commitRes.ok) {
          const errBody = await commitRes.json().catch(() => ({}));
          throw new Error(
            errBody?.message ||
              'Failed to finalize file upload. Please try again.',
          );
        }
      }

      setProgress(100);

      // Step 3: Complete
      const completeRes = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/content/upload/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ e: email, c_id: content_id }),
        },
      );

      const completeData = await completeRes.json();
      if (!completeRes.ok) {
        throw new Error(
          completeData?.error || 'Failed to finalize upload. Please try again.',
        );
      }

      setUploadedUrl(completeData.content_url);
      setUploadState('complete');

      // call the callback
      onUploadSuccess({
        ...contentUploadDetails,
        title: title,
        config: {
          allow_view: config.allowView,
          allow_download: config.allowDownload,
          allow_print: config.allowPrint,
        },
        url: completeData.content_url,
      });
    } catch (err) {
      setUploadState('idle');
      setProgress(0);
      setError(
        err?.message ||
          'Upload failed. Please check your connection and try again.',
      );
    }
  };

  const handleCopy = async () => {
    if (!uploadedUrl) return;
    try {
      await navigator.clipboard.writeText(uploadedUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = uploadedUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  const FileIcon = file ? getFileIcon(file) : File;

  const canUpload =
    Boolean(file) && Boolean(title.trim()) && uploadState === 'idle';

  return (
    <motion.div
      className={cssClass['backdrop']}
      onClick={handleClose}
      variants={animationVariants.backdrop}
      initial="hidden"
      animate="visible"
      exit="hidden"
      key="backdrop"
    >
      <motion.div
        className={cssClass['modal']}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-modal-title"
        onClick={(e) => e.stopPropagation()}
        variants={animationVariants.modal}
        initial="hidden"
        animate="visible"
        exit="hidden"
        key="modal"
      >
        {/* Header */}
        <div className={cssClass['header']}>
          <div>
            <h2 id="upload-modal-title" className={cssClass['header-title']}>
              Upload file
            </h2>
            <p className={cssClass['header-desc']}>
              Add a file and configure how it can be used.
            </p>
          </div>

          <button
            type="button"
            className={cssClass['icon-btn']}
            onClick={handleClose}
            disabled={uploadState === 'uploading'}
            aria-label="Close upload dialog"
          >
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        {uploadState !== 'complete' ? (
          <>
            <div className={cssClass['content']}>
              {/* Title */}
              <div className={cssClass['field']}>
                <label htmlFor="file-title" className={cssClass['field-label']}>
                  Title
                </label>
                <input
                  id="file-title"
                  type="text"
                  className={cssClass['field-input']}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Give your file a name"
                  disabled={uploadState === 'uploading'}
                  autoFocus
                />
              </div>

              {/* Dropzone */}
              <div
                className={`${cssClass['dropzone']} ${
                  dragging ? cssClass['dropzone-active'] : ''
                } ${file ? cssClass['dropzone-selected'] : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  if (uploadState === 'idle') setDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => {
                  if (uploadState === 'idle') inputRef.current?.click();
                }}
                role="button"
                tabIndex={uploadState === 'idle' ? 0 : -1}
                aria-label="Choose a file or drag and drop one here"
                onKeyDown={(event) => {
                  if (
                    (event.key === 'Enter' || event.key === ' ') &&
                    uploadState === 'idle'
                  ) {
                    event.preventDefault();
                    inputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  className={cssClass['hidden-input']}
                  onChange={handleFileChange}
                  disabled={uploadState === 'uploading'}
                  accept="
                      application/pdf,
                      text/html,
                      text/markdown,
                      text/plain,
                      text/x-python,
                      text/css,
                      application/json,
                      application/xml,
                      application/javascript,
                      application/zip,
                      application/x-zip-compressed,
                      application/gzip,
                      application/x-7z-compressed,
                      application/x-rar-compressed,
                      application/x-tar
                    "
                />

                {!file ? (
                  <>
                    <div className={cssClass['upload-icon']}>
                      <UploadCloud size={20} strokeWidth={1.7} />
                    </div>
                    <div className={cssClass['dropzone-text']}>
                      <span>
                        <strong>Choose a file</strong> or drag it here
                      </span>
                      <small>
                        PDF, HTML, Markdown, TXT, Python, JSON, XML, JavaScript,
                        ZIP, GZIP, 7Z, RAR, TAR up to 1 GB
                      </small>
                    </div>
                  </>
                ) : (
                  <AnimatePresence mode="wait">
                    <motion.div
                      className={cssClass['file-card']}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className={cssClass['file-preview']}>
                        <FileIcon size={22} strokeWidth={1.6} />
                      </div>
                      <div className={cssClass['file-info']}>
                        <span className={cssClass['file-name']}>
                          {file.name}
                        </span>
                        <span className={cssClass['file-meta']}>
                          {formatFileSize(file.size)} · {file.type || 'File'}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={cssClass['remove-file']}
                        onClick={() => {
                          setFile(null);
                          if (inputRef.current) inputRef.current.value = '';
                        }}
                        disabled={uploadState === 'uploading'}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X size={16} />
                      </button>
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>

              {/* Configuration */}
              {/* <div className={cssClass['config']}>
                <button
                  type="button"
                  className={cssClass['config-trigger']}
                  onClick={() => setConfigOpen((v) => !v)}
                  aria-expanded={configOpen}
                  aria-controls="upload-options"
                >
                  <div className={cssClass['config-trigger-text']}>
                    <span>Access &amp; permissions</span>
                    <small>
                      {[
                        config.allowView && 'View',
                        config.allowDownload && 'Download',
                        config.allowPrint && 'Print',
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'No permissions'}
                    </small>
                  </div>
                  <motion.span
                    className={cssClass['config-trigger-icon']}
                    animate={{ rotate: configOpen ? 180 : 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    <ChevronDown size={17} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {configOpen && (
                    <motion.div
                      id="upload-options"
                      className={cssClass['config-body']}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PermissionToggle
                        label="Allow viewing"
                        description="Users can open and view the file"
                        checked={config.allowView}
                        onChange={(value) =>
                          setConfig((c) => ({ ...c, allowView: value }))
                        }
                      />
                      <PermissionToggle
                        label="Allow downloading"
                        description="Users can save a copy"
                        checked={config.allowDownload}
                        onChange={(value) =>
                          setConfig((c) => ({ ...c, allowDownload: value }))
                        }
                      />
                      <PermissionToggle
                        label="Allow printing"
                        description="Users can print the file"
                        checked={config.allowPrint}
                        onChange={(value) =>
                          setConfig((c) => ({ ...c, allowPrint: value }))
                        }
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>*/}

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    className={cssClass['error-msg']}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.15 }}
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Upload progress */}
              <AnimatePresence>
                {uploadState === 'uploading' && (
                  <motion.div
                    className={cssClass['progress-section']}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className={cssClass['progress-header']}>
                      <span>Uploading...</span>
                      <span>{progress}%</span>
                    </div>
                    <div
                      className={cssClass['progress-track']}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={progress}
                      aria-label="File upload progress"
                    >
                      <motion.div
                        className={cssClass['progress-bar']}
                        animate={{ width: `${progress}%` }}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className={cssClass['footer']}>
              <button
                type="button"
                className={cssClass['secondary-btn']}
                onClick={handleClose}
                disabled={uploadState === 'uploading'}
              >
                Cancel
              </button>
              <button
                type="button"
                className={cssClass['primary-btn']}
                onClick={handleUpload}
                disabled={!canUpload}
              >
                {uploadState === 'uploading' ? (
                  <>
                    <Loader2 className={cssClass['spinner']} size={16} />
                    Uploading
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </>
        ) : (
          /* Success */
          <motion.div
            className={cssClass['success']}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <div className={cssClass['success-icon']}>
              <Check size={21} strokeWidth={2} />
            </div>
            <h3 className={cssClass['success-title']}>File uploaded</h3>
            <p className={cssClass['success-desc']}>
              Your file is ready to share.
            </p>

            <div className={cssClass['url-box']}>
              <span className={cssClass['url-text']} title={uploadedUrl}>
                {uploadedUrl}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className={cssClass['copy-btn']}
                aria-label="Copy file link"
              >
                {copied ? <Check size={16} /> : <Clipboard size={16} />}
              </button>
            </div>

            <button
              type="button"
              className={cssClass['done-btn']}
              onClick={handleClose}
            >
              Done
            </button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

function PermissionToggle({ label, description, checked, onChange }) {
  return (
    <label className={cssClass['permission']}>
      <div className={cssClass['permission-text']}>
        <span>{label}</span>
        <small>{description}</small>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={`${cssClass['switch']} ${
          checked ? cssClass['switch-on'] : ''
        }`}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </label>
  );
}

ContentUploadModel.propTypes = {
  onClose: PropTypes.func,
};

ContentUploadModel.defaultProps = {
  onClose: null,
};

export default ContentUploadModel;